import { useEffect, useMemo, useState } from "react";
import { AlertCircle, LoaderCircle, PoundSterling } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { setTitle } from "@/hooks/useHeader.ts";
import { z } from "zod";
import { TFunction } from "i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createPayment } from "@/api/wallet.ts";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert.tsx";
import { useTranslation } from "@/i18n";

const presetAmounts = [1, 2, 3, 5, 10, 20, 50];

const createFormSchema = (t: TFunction) =>
  z.object({
    amount: z
      .string()
      .refine(
        (val) => val && !isNaN(parseFloat(val)) && parseFloat(val) >= 1.0,
        {
          message: t("validation.invalidAmount"),
        },
      ),
  });

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

const Deposit = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const { t } = useTranslation();
  const formSchema = useMemo(() => createFormSchema(t), [t]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  });

  const amount = form.watch("amount");

  useEffect(() => {
    setTitle(t("deposit.title"));
  }, [t]);

  const handlePresetAmount = (preset: string) => {
    form.setValue("amount", preset);
    form.clearErrors();
  };

  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    try {
      const response = await createPayment({ amount: values.amount! });
      window.location.replace(response.data.redirect_url);

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e: unknown) {
      setError(t("deposit.redirectFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-md min-w-[45vw] flex-col">
      <Card className="border-none shadow-lg">
        <CardHeader>
          <CardTitle className="text-xl">{t("deposit.heading")}</CardTitle>
          <CardDescription>{t("deposit.description")}</CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mx-auto my-4 w-fit">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("deposit.failed")}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="space-y-2">
                <Label className="text-base">{t("deposit.quickAmount")}</Label>
                <div className="flex flex-wrap gap-2">
                  {presetAmounts.map((preset) => (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className={cn(
                        "bg-card text-card-foreground min-w-[60px] flex-1",
                        parseFloat(amount) === preset &&
                          "bg-primary text-primary-foreground",
                      )}
                      onClick={() => handlePresetAmount(`${preset}`)}
                    >
                      {t("common.price", { amount: preset })}
                    </Button>
                  ))}
                </div>
              </div>
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("form.amount")}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <PoundSterling className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform text-gray-500" />
                        <Input
                          id="amount"
                          type="string"
                          placeholder={t("form.amountPlaceholder")}
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            setError("");
                          }}
                          className="pl-10"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                className="w-full py-6 text-lg"
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center">
                    <LoaderCircle className="animate-spin gap-2" />
                    {t("common.processing")}
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    {t("deposit.submit")}
                  </div>
                )}
              </Button>
            </form>
          </Form>
        </CardContent>
        <CardFooter className="text-muted-foreground text-center text-xs">
          {t("deposit.processingNote")}
        </CardFooter>
      </Card>
    </div>
  );
};

export default Deposit;
